import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IAPIResponse, StatusCode } from "api/protocols";
import { UserActive, UserLogout } from "api/rpc";
import { IUser, IUserActive } from "redux/reducers/models/user";
import { IFacility } from "redux/reducers/models/facility";
import { GetFacility, IFacilityTeeSheetSettings } from "api/rpc/2024-04/customer/facility";
import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";
import { GetFacilityGuest } from "api/rpc/2024-04/guest/facility";
import { useParams, useRouteMatch } from "react-router";
import { sortFacilities } from "redux/reducers/clientPortal";
import moment from "moment";
import axios, { CancelToken } from "axios";
import { IBooking, ITeeTime } from "redux/reducers/models/teetime";
import { ICart } from "redux/reducers/models/cart";
import Popup from "components/popup/Popup";
import Portal from "elements/Portal";

export interface IFacilityPortalExtended extends IFacility {
  teeSheetSettings?: IFacilityTeeSheetSettings;
}

interface IExtendedBooking extends Partial<IBooking> {
  paymentMethodId?: number;
  cart?: ICart;
}

interface IExtendedTeeTime extends ITeeTime {
  formattedStartTime?: string;
  formattedDate?: string;
}

type TTeeOnPortalState = {
  user: IUserActive;
  facilities: Array<IFacility>;
  selectedFacilities: Array<IFacilityPortalExtended>;
  selectedDate: { date: Date; dayOfWeekNumber: number };
  selectedTeeTime: {
    teeTime: IExtendedTeeTime;
    facility: IFacilityPortalExtended;
    booking: IExtendedBooking;
    lock: {
      lockoutDuration: number;
      lockStartTime: number;
      expireTime: string;
    };
  };
  waitlist_reference_id: string;
};

type TTeeOnPortalContext = {
  teeOnPortalState: TTeeOnPortalState;
  updateTeeOnPortal: (state: Partial<TTeeOnPortalState>) => void;
};

const defaultState: TTeeOnPortalContext = {
  teeOnPortalState: null,
  updateTeeOnPortal: null,
};

const TeeOnPortalCreateContext = createContext<TTeeOnPortalContext>(defaultState);
export const useTeeOnPortalContext = () => useContext(TeeOnPortalCreateContext);

export const TeeOnPortalContext = (props: { children: ReactNode }) => {
  const { t } = useTranslation();
  const baseUrl = "/booking/teetimes/:facilityShortName";
  const facilityShortName = useRouteMatch<{ facilityShortName: string }>(baseUrl)?.params?.facilityShortName ?? "";
  const urlParams = new URLSearchParams(window.location.search);
  const waitlist_reference_id = urlParams.get("waitlist_reference_id");
  const [adminUserActive, setAdminUserActive] = useState<boolean>(false);
  const [teeOnPortalState, setTeeOnPortalState] = useState<TTeeOnPortalState>({
    user: null,
    facilities: null,
    selectedFacilities: null,
    selectedDate: { date: new Date(), dayOfWeekNumber: moment(new Date()).day() === 0 ? 7 : moment(new Date()).day() },
    selectedTeeTime: null,
    waitlist_reference_id: null,
  });
  const dispatch = useAppDispatch();

  function updateTeeOnPortal(newRegisterState: Partial<TTeeOnPortalState>) {
    setTeeOnPortalState((prevState: TTeeOnPortalState) => {
      return { ...prevState, ...newRegisterState };
    });
  }

  useEffect(() => {
    const source = axios.CancelToken.source();
    void validateActiveUser(source.token);
    if (waitlist_reference_id) {
      updateTeeOnPortal({ waitlist_reference_id });
    }
    return () => source.cancel();
  }, [teeOnPortalState.user]);

  async function validateActiveUser(cancelToken: CancelToken) {
    let activeUser = teeOnPortalState?.user;
    if (!activeUser) {
      const activeUserRes = await UserActive(false);

      //Active user confirmed, set in context state
      if (activeUserRes.status === StatusCode.OK) {
        activeUser = activeUserRes?.data;
      } else {
        // No active user, set as null
        activeUser = null;
      }

      //Display popup to log out user if an admin user is logged in
      if (activeUserRes?.data?.user_type === 2 || activeUserRes?.data?.user_type === 3) {
        setAdminUserActive(true);
        return;
      }
    }

    let facilitiesRes: IAPIResponse<Array<IFacility>>;
    if (activeUser) {
      // User active, use customer endpoint
      facilitiesRes = await GetFacility(null, false, cancelToken);
    } else {
      // User not active, use guest endpoint
      facilitiesRes = await GetFacilityGuest(null, false, cancelToken);
    }
    if (!facilitiesRes || facilitiesRes?.status !== StatusCode.OK) {
      if (cancelToken && cancelToken?.reason) {
        return;
      }
      dispatch(showError(t("Error getting facilities")));
      return;
    }

    //Sort facilities alphabetically and by favourites
    const sortedFacilities = sortFacilities(facilitiesRes?.data);
    let selectedFacilities: Array<IFacility> = [];
    if (facilityShortName) {
      //Select default facility
      const currentFacility = sortedFacilities?.find(facility => facility?.short_name === facilityShortName);
      if (currentFacility) {
        selectedFacilities = [currentFacility];
      } else {
        selectedFacilities = [{ ...sortedFacilities[0] }];
      }
    } else if (sortedFacilities?.length > 0) {
      selectedFacilities = [{ ...sortedFacilities[0] }];
    }
    updateTeeOnPortal({ facilities: sortedFacilities, selectedFacilities: selectedFacilities, user: activeUser });
  }

  async function logoutUser() {
    const logoutRes = await UserLogout(true);

    if (logoutRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error logging out")));
    }

    location.reload();
  }

  return (
    <TeeOnPortalCreateContext.Provider value={{ teeOnPortalState, updateTeeOnPortal }}>
      {props?.children}
      <Portal isMounted={adminUserActive}>
        <Popup
          open={adminUserActive}
          type="warning"
          title={t("Admin Account Detected")}
          description={t("Looks like you are logged into an admin account. Click logout below to view the tee times")}
          onOk={logoutUser}
          okText={t("Logout")}
        />
      </Portal>
    </TeeOnPortalCreateContext.Provider>
  );
};
